import React, { useState, useEffect } from 'react';
import { Lock, ArrowLeft, KeyRound, AlertCircle, RefreshCw } from 'lucide-react';

interface AdminLoginProps {
  onLoginSuccess: (token: string, user: { email: string; name: string }) => void;
  onBackToSite: () => void;
}

export const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess, onBackToSite }) => {
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [appEnv, setAppEnv] = useState<string>('production');
  const [gisLoaded, setGisLoaded] = useState<boolean>(false);

  const isLocalDev = 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ||
    appEnv === 'development';

  useEffect(() => {
    // 1. Fetch Google Client ID and App Environment from backend
    fetch('/api/auth/config')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.google_client_id) {
          setGoogleClientId(data.google_client_id);
        }
        if (data?.app_env) {
          setAppEnv(data.app_env);
        }
      })
      .catch(() => {
        // Backend offline or local fallback
        setAppEnv('development');
      });
  }, []);

  useEffect(() => {
    if (!googleClientId) return;

    // 2. Load Google Identity Services script if client ID exists
    const scriptId = 'google-gsi-client';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => setGisLoaded(true);
      document.body.appendChild(script);
    } else {
      setGisLoaded(true);
    }
  }, [googleClientId]);

  useEffect(() => {
    if (!gisLoaded || !googleClientId || !(window as any).google?.accounts?.id) return;

    try {
      (window as any).google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response: { credential: string }) => {
          setLoading(true);
          setError('');
          try {
            const res = await fetch('/api/auth/google', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ credential: response.credential }),
            });

            if (res.ok) {
              const data = await res.json();
              onLoginSuccess(data.token, data.user);
            } else {
              const errData = await res.json();
              setError(errData.detail || 'Access denied: Google account is not authorized as administrator.');
            }
          } catch {
            setError('Backend authentication service connection error');
          } finally {
            setLoading(false);
          }
        },
      });

      const container = document.getElementById('google-signin-button-slot');
      if (container) {
        (window as any).google.accounts.id.renderButton(container, {
          theme: 'filled_black',
          size: 'large',
          width: container.offsetWidth || 360,
          text: 'signin_with',
          shape: 'rectangular',
        });
      }
    } catch (e) {
      console.warn('GIS initialization error:', e);
    }
  }, [gisLoaded, googleClientId, onLoginSuccess]);

  const handleDevBypass = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/auth/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: 'dev-admin-token-bypass' }),
      });

      if (response.ok) {
        const data = await response.json();
        onLoginSuccess(data.token, data.user);
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Dev login bypass rejected');
      }
    } catch {
      // Mock local session if backend API is not running
      onLoginSuccess('mock-admin-jwt-token', {
        email: 'thomas.l.desrosiers@gmail.com',
        name: 'Thomas Desrosiers (Offline Dev)',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem', background: 'var(--bg-primary)' }}>
      <div className="bg-ambience" />
      <div style={{ maxWidth: '440px', width: '100%', padding: '2.5rem', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-elevated)', position: 'relative', zIndex: 1 }}>
        <button
          onClick={onBackToSite}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '1.5rem' }}
        >
          <ArrowLeft size={14} />
          <span>Back to Live Site</span>
        </button>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ width: '48px', height: '48px', margin: '0 auto 1rem', borderRadius: '50%', background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-gold)' }}>
            <Lock size={22} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
            Admin Portal
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Restricted access for <span style={{ color: 'var(--accent-gold)', fontWeight: 600 }}>thomas.l.desrosiers@gmail.com</span>
          </p>
        </div>

        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', borderRadius: 'var(--radius-sm)', background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', fontSize: '0.85rem', marginBottom: '1.25rem', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Official Google Sign-In Button */}
          <div id="google-signin-button-slot" style={{ minHeight: '44px', display: 'flex', justifyContent: 'center' }} />

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem', color: 'var(--accent-gold)', fontSize: '0.85rem' }}>
              <RefreshCw size={16} className="spin" />
              <span>Verifying authorization...</span>
            </div>
          )}

          {/* Local Developer Bypass: Only displayed when running in local development */}
          {isLocalDev && (
            <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '1rem' }}>
              <button
                onClick={handleDevBypass}
                disabled={loading}
                className="btn btn-secondary"
                style={{ width: '100%', padding: '0.65rem', justifyContent: 'center', fontSize: '0.8rem', color: 'var(--accent-gold)' }}
              >
                <KeyRound size={14} />
                <span>Local Dev 1-Click Bypass</span>
              </button>
              <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '0.4rem' }}>
                (Only visible on localhost / development environment)
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


